// Tjänst × ort för foto (etapp 3). En post per kombination av tjänst och
// storstad, med unik lokal vinkel så att sidan gör ett eget jobb och inte
// dubblerar vare sig ortssidan (/foto/stockholm) eller tjänstesidan.
// Sanningskälla för generatorn i src/lib/seo/fotoServiceCityRoute.tsx.

import type { PhotoCategory } from "@/content/photo-gallery";

export interface FotoServiceCity {
  slug: string;
  path: string;
  serviceKey: string;
  citySlug: "stockholm" | "goteborg" | "malmo";
  city: string;
  region: string;
  crumb: string;
  nav: string;
  metaTitle: string;
  description: string;
  eyebrow: string;
  h1: string;
  lede: string;
  question: string;
  quickAnswer: string;
  serviceType: string;
  gallery: PhotoCategory | "alla";
  galleryHeading: string;
  knowsAbout: string[];
  /** Unik lokal vinkel: var, vilka branscher, vad som är särskilt på orten. */
  localAngle: { heading: string; paragraphs: string[] };
  /** Unikt upplägg för tjänsten på orten: logistik, tider, platser. */
  logistics: { heading: string; paragraphs: string[] };
  bullets: { title: string; body: string }[];
  faq: { q: string; a: string }[];
  related: { label: string; to: string }[];
}

const DELIVERY =
  "Retuscherade bilder levereras inom 3–5 arbetsdagar och fri användningsrätt i egna kanaler ingår.";
const QUOTE =
  "Priset offereras per uppdrag utifrån tid på plats, antal färdiga bilder, retuschnivå och användningsrätt. Kontakt: Fenny, hey@fenny.se.";

function related(exclude: string): { label: string; to: string }[] {
  return [
    { label: "Foto — översikt", to: "/foto" },
    { label: "Vad kostar en fotograf?", to: "/foto/vad-kostar-en-fotograf" },
    { label: "Företagsfotograf", to: "/foto/foretagsfotograf" },
    { label: "Porträttfotograf", to: "/foto/portrattfotograf" },
    { label: "Bildindex 2026", to: "/branschstudier/bildindex-2026" },
  ].filter((r) => r.to !== exclude);
}

export const fotoServiceCities: FotoServiceCity[] = [
  // ---------------------------------------------------------------- Stockholm
  {
    slug: "foretagsfotograf-stockholm",
    path: "/foto/foretagsfotograf-stockholm",
    serviceKey: "foretagsfoto",
    citySlug: "stockholm",
    city: "Stockholm",
    region: "Stockholms län",
    crumb: "Företagsfotograf Stockholm",
    nav: "Företagsfotograf Stockholm",
    metaTitle: "Företagsfotograf Stockholm — porträtt och kampanj | Fenny",
    description:
      "Företagsfotograf i Stockholm för porträtt, miljöbilder och kampanjbilder. Studio i staden eller mobil studio på ert kontor. Offert inom en arbetsdag.",
    eyebrow: "Företagsfotograf Stockholm",
    h1: "Företagsfotograf i Stockholm — ett bildmanér som håller över tid",
    lede:
      "Vi fotograferar hela organisationen på en dag: porträtt med samma ljus, miljöbilder från kontoret och de kampanjbilder som sajten faktiskt behöver.",
    question: "Vad gör en företagsfotograf i Stockholm och vad kostar det?",
    quickAnswer:
      "En företagsfotograf i Stockholm fotograferar porträtt, miljöbilder och kampanjbilder för företagets egna kanaler. Fenny har studio i Stockholm och bygger mobil studio på ert kontor i innerstaden, Solna, Kista eller Sickla. En halvdag räcker till 10–20 porträtt, en heldag till 20–40. " +
      DELIVERY +
      " " +
      QUOTE,
    serviceType: "Företagsfotografering i Stockholm",
    gallery: "portratt",
    galleryHeading: "Företagsbilder från Stockholmsuppdrag",
    knowsAbout: [
      "Företagsfotograf Stockholm",
      "Företagsporträtt Stockholm",
      "Miljöbilder kontor",
      "Kampanjfoto Stockholm",
    ],
    localAngle: {
      heading: "Så ser företagsfotografering i Stockholm ut",
      paragraphs: [
        "Uppdragen i Stockholm kommer oftast från bolag där marknadsavdelningen sitter i innerstaden medan medarbetarna är utspridda över Solna, Kista, Sickla och Arlandastråket. Lösningen är sällan att alla åker till en studio — den är att fotografen kommer till kontoret med mobil studio och betar av avdelning för avdelning.",
        "Kontorslandskapen i Stockholm är ofta glasade och ljusstarka, vilket ser bra ut men blandar dagsljus med LED-armaturer i olika färgtemperaturer. Vi mäter av rummet och sätter ett eget ljus, annars skiftar hudtonerna mellan våningsplanen och bildbanken hänger inte ihop.",
        "Många Stockholmsbolag har dessutom en engelsk koncernguide att förhålla sig till. Vi arbetar direkt mot brand book och levererar filer namngivna och beskurna för koncernens DAM.",
      ],
    },
    logistics: {
      heading: "Upplägg och logistik i Stockholm",
      paragraphs: [
        "Riggning tar cirka 45 minuter och kräver ett rum på ungefär tre gånger tre meter med ett eluttag. Vi lägger ett tidsschema i femminutersluckor per avdelning så att ingen väntar i korridoren.",
        "Resa inom Stockholms län ingår i offerten — innerstaden, Solna, Kista, Sickla, Nacka, Täby, Bromma och Arlandaområdet. Uppsala och Västerås ligger inom en timme och läggs ofta i samma produktionsvecka.",
        "Behövs studio i stället — för ren bakgrund, produkter eller kampanj — fotograferar vi i vår studio i Stockholm och ni ser bilderna direkt på skärm under passet.",
      ],
    },
    bullets: [
      { title: "Hela kontoret på en dag", body: "20–40 medarbetare hinns med under en heldag med mobil studio på plats." },
      { title: "Samma ljus varje gång", body: "Ljusuppsättningen dokumenteras så att nyanställda kan matchas in i serien senare." },
      { title: "Resa ingår i länet", body: "Innerstaden, Solna, Kista, Sickla, Nacka, Täby och Arlanda utan reseavgift." },
      { title: "Film samma dag", body: "Fenny är i grunden ett filmbolag — rörligt kan produceras parallellt och sänker totalkostnaden." },
    ],
    faq: [
      { q: "Vad kostar en företagsfotograf i Stockholm?", a: "Priset sätts per pass: en halvdag räcker till 10–20 porträtt och en heldag till 20–40. Eftersom rigg, resa och ljussättning kostar detsamma oavsett antal sjunker kostnaden per person snabbt med antalet medarbetare samma dag. Ni får fast pris innan bokning." },
      { q: "Kan ni fotografera på vårt kontor?", a: "Ja. Vi tar med mobil studio och bygger upp fotoplatsen i ett konferensrum — det behövs ungefär tre gånger tre meter och 45 minuters riggning." },
      { q: "Hur snabbt får vi bilderna?", a: "Ett urvalsgalleri kommer inom två arbetsdagar och retuscherade bilder inom 3–5 arbetsdagar." },
      { q: "Kan nyanställda fotograferas in senare?", a: "Ja. Vi dokumenterar ljus, avstånd och beskärning, så kompletteringar matchar originalserien även ett år senare." },
      { q: "Arbetar ni på engelska?", a: "Ja. Brief, regi på plats och leverans kan skötas helt på engelska." },
    ],
    related: related("/foto/foretagsfotograf-stockholm"),
  },
  {
    slug: "portrattfotograf-stockholm",
    path: "/foto/portrattfotograf-stockholm",
    serviceKey: "portratt",
    citySlug: "stockholm",
    city: "Stockholm",
    region: "Stockholms län",
    crumb: "Porträttfotograf Stockholm",
    nav: "Porträttfotograf Stockholm",
    metaTitle: "Porträttfotograf Stockholm — headshots hos er | Fenny",
    description:
      "Porträttfotograf i Stockholm för headshots och miljöporträtt. Studio i staden eller på ert kontor. Retuscherade bilder inom 3–5 arbetsdagar.",
    eyebrow: "Porträttfotograf Stockholm",
    h1: "Porträttfotograf i Stockholm — porträtt som fungerar i alla kanaler",
    lede:
      "Headshots för sajt och LinkedIn, miljöporträtt för pressrum och årsredovisning — fotograferade så att samma bild håller i kvadrat, stående och liggande.",
    question: "Vad kostar en porträttfotograf i Stockholm?",
    quickAnswer:
      "En porträttfotograf i Stockholm prissätts per pass, inte per bild. Ett enskilt porträttpass i studio tar 45–90 minuter, en halvdag räcker till 10–20 personer och en heldag till 20–40. Fenny fotograferar i studio i Stockholm eller med mobil studio på ert kontor. " +
      DELIVERY +
      " " +
      QUOTE,
    serviceType: "Porträttfotografering i Stockholm",
    gallery: "portratt",
    galleryHeading: "Porträtt från Stockholm",
    knowsAbout: [
      "Porträttfotograf Stockholm",
      "Headshots Stockholm",
      "Miljöporträtt",
      "Ledningsgruppsporträtt",
    ],
    localAngle: {
      heading: "Porträtt i Stockholm — vad kunderna faktiskt beställer",
      paragraphs: [
        "De vanligaste beställningarna i Stockholm är tre: enskilda porträtt av en ny vd eller partner inför en publicering, ledningsgruppsporträtt inför en rapport, och hela organisationen i samband med en sajtomgörning.",
        "Enskilda porträtt bokas ofta med kort varsel och kan i regel läggas in inom veckan. Större serier planeras två till tre veckor fram, eftersom kalendrar för trettio personer ska synkas.",
        "För byråer, advokatbyråer och konsultbolag i innerstaden är kravet nästan alltid detsamma: neutral bakgrund, samma bildutsnitt för alla, och ett friare miljöporträtt som alternativ för dem som exponeras utåt.",
      ],
    },
    logistics: {
      heading: "I studio eller hos er",
      paragraphs: [
        "I studio har vi full kontroll på bakgrund och ljus och kan visa bilderna på stor skärm medan passet pågår — bra när porträttet ska godkännas av flera.",
        "På plats hos er sparar ni restid för medarbetarna. Vi bygger mobil studio i ett konferensrum och håller ett schema med 5–10 minuter per person för tight headshot, 10–15 minuter när ett miljöporträtt ingår.",
        "Retuschen hålls varsam: hy och ljus jämnas ut, ansiktsdrag ändras inte. Ni får bilderna i kvadrat för intranät och LinkedIn, stående för sajten och högupplöst för tryck.",
      ],
    },
    bullets: [
      { title: "5–10 minuter per person", body: "Tight schema gör att en heldag räcker till 20–40 porträtt." },
      { title: "Direkt på skärm", body: "Varje person ser ett par bilder direkt, vilket gör även fotoskygga trygga." },
      { title: "Alla format", body: "Kvadrat, stående och liggande beskärningar levereras från samma tagning." },
      { title: "Varsam retusch", body: "Hy och ljus jämnas ut — ansiktsdrag rörs inte." },
    ],
    faq: [
      { q: "Vad kostar ett porträttpass i Stockholm?", a: "Priset sätts per pass. Ett enskilt porträttpass i studio tar 45–90 minuter, en halvdag räcker till 10–20 personer och en heldag till 20–40. Ni får fast pris innan bokning." },
      { q: "Hur lång tid tar ett porträtt?", a: "5–10 minuter per person för ett tight headshot och 10–15 minuter när ett miljöporträtt ingår." },
      { q: "Har ni studio i Stockholm?", a: "Ja, vi fotograferar i studio i Stockholm och kan även bygga mobil studio i era lokaler." },
      { q: "Kan vi boka med kort varsel?", a: "Enskilda porträtt kan ofta läggas in inom veckan; större serier planeras två till tre veckor fram." },
    ],
    related: related("/foto/portrattfotograf-stockholm"),
  },
  {
    slug: "produktfotograf-stockholm",
    path: "/foto/produktfotograf-stockholm",
    serviceKey: "produkt",
    citySlug: "stockholm",
    city: "Stockholm",
    region: "Stockholms län",
    crumb: "Produktfotograf Stockholm",
    nav: "Produktfotograf Stockholm",
    metaTitle: "Produktfotograf Stockholm — e-handel och packshots | Fenny",
    description:
      "Produktfotograf i Stockholm för e-handel, packshots och miljöbilder. Studio i staden, tethering till skärm och leverans i marknadsplatsernas format.",
    eyebrow: "Produktfotograf Stockholm",
    h1: "Produktfotograf i Stockholm — bilder som säljer i alla kanaler",
    lede:
      "Packshots på vitt för e-handel och marknadsplatser, miljöbilder för kampanj och detaljbilder som svarar på frågorna kunden annars mejlar om.",
    question: "Vad kostar produktfotografering i Stockholm?",
    quickAnswer:
      "Produktfoto prissätts per produkt när upplägget är repetitivt och per dag när varje bild är unik. En studiodag i Stockholm räcker till 15–40 packshots beroende på antal vinklar och hur mycket styling som krävs. " +
      DELIVERY +
      " " +
      QUOTE,
    serviceType: "Produktfotografering i Stockholm",
    gallery: "studio",
    galleryHeading: "Produktbilder ur portfolion",
    knowsAbout: [
      "Produktfotograf Stockholm",
      "Packshots",
      "E-handelsbilder",
      "Studiofotografering Stockholm",
    ],
    localAngle: {
      heading: "E-handel i Stockholm ställer egna krav",
      paragraphs: [
        "Många Stockholmsbolag säljer både i egen butik och på marknadsplatser som ställer hårda krav på bakgrund, marginaler och bildstorlek. Vi fotograferar en gång och exporterar i de format varje kanal kräver, i stället för att beskära om i efterhand.",
        "Sortiment som uppdateras ofta behöver ett fotomanér som går att upprepa. Vi dokumenterar ljus, avstånd och vinklar så att nästa kollektion kan fotograferas identiskt — även om det sker ett halvår senare.",
        "Produkter som ska säljas i både B2B- och konsumentkanal behöver ofta två serier: rena packshots för katalog och prislista, miljöbilder för kampanj och sociala medier. De ligger i samma dag men planeras separat.",
      ],
    },
    logistics: {
      heading: "Så planeras studiodagen",
      paragraphs: [
        "Produkterna skickas eller lämnas i studion dagen före. Vi går igenom bildlistan — antal produkter, antal vinklar per produkt, vilka detaljer som ska med — och räknar fram hur många som ryms per dag.",
        "Under dagen kopplas kameran till skärm så att ni kan godkänna bilderna löpande, på plats eller på distans. Det sparar en hel korrigeringsrunda.",
        "Efterarbetet innehåller frilägg, färgmatchning mot faktisk produkt och export i webb- och tryckupplösning. Vid stora sorteringar levereras filerna namngivna efter era artikelnummer.",
      ],
    },
    bullets: [
      { title: "15–40 packshots per dag", body: "Antalet styrs av vinklar per produkt och hur mycket styling som behövs." },
      { title: "Godkännande live", body: "Tethering till skärm gör att bilderna kan godkännas medan de tas." },
      { title: "Rätt format direkt", body: "Export enligt marknadsplatsernas krav på bakgrund, marginal och storlek." },
      { title: "Upprepbart manér", body: "Ljus och vinklar dokumenteras så nästa kollektion matchar." },
    ],
    faq: [
      { q: "Vad kostar produktfoto i Stockholm?", a: "Repetitiva upplägg prissätts per produkt, unika bilder per dag. En studiodag räcker till 15–40 packshots beroende på vinklar och styling. Ni får fast pris innan bokning." },
      { q: "Måste vi vara med i studion?", a: "Nej. Kameran kopplas till skärm och bilderna kan godkännas på distans under dagen." },
      { q: "Fotograferar ni även miljöbilder?", a: "Ja, rena packshots och miljöbilder kan produceras samma dag men planeras som två separata serier." },
      { q: "Hur levereras filerna?", a: "Frilagda och färgmatchade, i webb- och tryckupplösning, namngivna efter era artikelnummer vid större sortiment." },
    ],
    related: related("/foto/produktfotograf-stockholm"),
  },
  {
    slug: "eventfotograf-stockholm",
    path: "/foto/eventfotograf-stockholm",
    serviceKey: "event",
    citySlug: "stockholm",
    city: "Stockholm",
    region: "Stockholms län",
    crumb: "Eventfotograf Stockholm",
    nav: "Eventfotograf Stockholm",
    metaTitle: "Eventfotograf Stockholm — konferens och mässa | Fenny",
    description:
      "Eventfotograf i Stockholm för konferenser, mässor, kundträffar och interna dagar. Första urval samma kväll, hela leveransen inom 3–5 arbetsdagar.",
    eyebrow: "Eventfotograf Stockholm",
    h1: "Eventfotograf i Stockholm — bilder ni kan publicera samma kväll",
    lede:
      "Konferenser, mässor, mingel och prisutdelningar dokumenterade så att ni får både snabba kanalbilder och en arkivserie som håller i nästa års material.",
    question: "Vad kostar en eventfotograf i Stockholm?",
    quickAnswer:
      "Eventfoto prissätts per timmar på plats. Ett halvdagspass ger normalt 60–100 redigerade bilder och en heldag 150–250. Ett första urval kan levereras samma kväll för publicering, resten inom 3–5 arbetsdagar. " +
      QUOTE,
    serviceType: "Eventfotografering i Stockholm",
    gallery: "kampanj",
    galleryHeading: "Eventbilder ur portfolion",
    knowsAbout: [
      "Eventfotograf Stockholm",
      "Konferensfotograf",
      "Mässfotograf",
      "Fotograf kundträff",
    ],
    localAngle: {
      heading: "Stockholms eventmiljöer",
      paragraphs: [
        "Konferensanläggningar i Stockholm är ofta mörka med starka scenljus och färgade LED-riggar. Vi fotograferar med snabba objektiv och blandljusteknik i stället för blixt rakt in i publiken, så att stämningen i rummet finns kvar i bilden.",
        "På mässorna i Älvsjö och Kistamässan gäller andra villkor: hård belysning, trängsel och begränsat utrymme i montern. Där planerar vi bildlistan i förväg — monter, möten, produktdemo, talare — så att inget missas när det är fullt.",
        "Mingel och prisutdelningar behöver en fotograf som rör sig obemärkt. Vi arbetar utan blixt när det går och tar hänsyn till att gäster ska kunna säga nej till att synas i publicerat material.",
      ],
    },
    logistics: {
      heading: "Upplägg under eventdagen",
      paragraphs: [
        "Inför eventet går vi igenom körschemat och markerar de ögonblick som absolut måste finnas: keynote, panel, prisutdelning, gruppbild, monterbilder. Resten fotograferas reportagemässigt.",
        "Under dagen kan ett första urval levereras löpande — färdiga bilder för sociala medier medan eventet fortfarande pågår. Det gör störst skillnad för konferenser som marknadsförs i realtid.",
        "Efter eventet väljer ni ur ett galleri och vi färgsätter urvalet. Bilderna levereras i liggande, stående och kvadratiska beskärningar så att samma bild kan gå i flera kanaler.",
      ],
    },
    bullets: [
      { title: "Bilder samma kväll", body: "Ett första urval kan levereras redan under eventet för publicering i realtid." },
      { title: "Utan blixt när det går", body: "Snabba objektiv och blandljus bevarar stämningen i rummet." },
      { title: "Bildlista i förväg", body: "Keynote, panel, monter och gruppbilder markeras i körschemat så inget missas." },
      { title: "Film parallellt", body: "Vi kan bemanna med filmteam samma dag för aftermovie och klipp till kanalerna." },
    ],
    faq: [
      { q: "Vad kostar en eventfotograf i Stockholm?", a: "Priset sätts per timmar på plats. En halvdag ger normalt 60–100 redigerade bilder och en heldag 150–250. Ni får fast pris innan bokning." },
      { q: "Kan vi få bilder under dagen?", a: "Ja. Ett första urval kan levereras löpande under eventet för publicering samma kväll." },
      { q: "Fotograferar ni utan blixt?", a: "Ja, vi arbetar med snabba objektiv och blandljus när ljuset tillåter, för att inte störa deltagarna." },
      { q: "Hanterar ni gäster som inte vill synas?", a: "Ja. Vi märker upp önskemål under dagen och håller de personerna utanför det publicerade urvalet." },
    ],
    related: related("/foto/eventfotograf-stockholm"),
  },

  // ---------------------------------------------------------------- Göteborg
  {
    slug: "foretagsfotograf-goteborg",
    path: "/foto/foretagsfotograf-goteborg",
    serviceKey: "foretagsfoto",
    citySlug: "goteborg",
    city: "Göteborg",
    region: "Västra Götaland",
    crumb: "Företagsfotograf Göteborg",
    nav: "Företagsfotograf Göteborg",
    metaTitle: "Företagsfotograf Göteborg — porträtt och kampanj | Fenny",
    description:
      "Företagsfotograf i Göteborg för porträtt, miljöbilder och kampanjbilder. Mobil studio på ert kontor i centrum, Lindholmen eller Mölndal.",
    eyebrow: "Företagsfotograf Göteborg",
    h1: "Företagsfotograf i Göteborg — från kontoret till produktionsgolvet",
    lede:
      "Porträtt av hela organisationen, miljöbilder i verkstad och hamn, och kampanjbilder som fungerar lika bra i offert som i annons.",
    question: "Vad gör en företagsfotograf i Göteborg?",
    quickAnswer:
      "En företagsfotograf i Göteborg fotograferar porträtt, miljöbilder och kampanjbilder för företagets egna kanaler. Fenny arbetar med mobil studio på plats i centrum, Lindholmen, Gårda, Mölndal och Hisingen, och är van vid produktionsmiljöer med skiftgång och skyddsrutiner. " +
      DELIVERY +
      " " +
      QUOTE,
    serviceType: "Företagsfotografering i Göteborg",
    gallery: "portratt",
    galleryHeading: "Företagsbilder ur portfolion",
    knowsAbout: [
      "Företagsfotograf Göteborg",
      "Industrifoto",
      "Företagsporträtt Göteborg",
      "Miljöbilder produktion",
    ],
    localAngle: {
      heading: "Industri, logistik och tech i samma stad",
      paragraphs: [
        "Göteborgsuppdragen skiljer sig från Stockholms genom att en stor del av bilderna tas i produktion: verkstäder, hamnterminaler, lager och testanläggningar. Där räcker inte ett porträttljus — vi blandar befintligt ljus med egna källor så att både maskiner och människor syns tydligt i samma bild.",
        "Lindholmen och Gårda står för den andra halvan: techbolag och konsulthus med öppna kontorslandskap där miljöporträtt och mötesbilder är det som efterfrågas.",
        "Bolag med flera anläggningar i regionen — Göteborg, Mölndal, Borås — planeras in i samma produktionsresa. Det håller nere resekostnaden och gör att bilderna från alla enheter ser likadana ut.",
      ],
    },
    logistics: {
      heading: "Att fotografera i produktionsmiljö",
      paragraphs: [
        "Skyddsutrustning, säkerhetsgenomgång och eskort löses före fotodagen tillsammans med er platsansvarige, så att ingen tid går åt på plats. Vi planerar kring skiftbyten så att produktionen inte behöver stanna.",
        "Porträtten tas i ett konferensrum med mobil studio — cirka 45 minuters riggning och ett rum på ungefär tre gånger tre meter — medan miljöbilderna tas ute i verksamheten enligt en bildlista vi går igenom i förväg.",
        "Sekretess hanteras genom att skärmar, ritningar och prototyper täcks eller flyttas innan bilden tas, och genom att ni godkänner urvalet innan något publiceras.",
      ],
    },
    bullets: [
      { title: "Van vid industrimiljö", body: "Skyddsutrustning, skiftgång och säkerhetsrutiner planeras in före fotodagen." },
      { title: "Kontor och produktion", body: "Porträtt i mobil studio och miljöbilder ute i verksamheten samma dag." },
      { title: "En resa, flera enheter", body: "Göteborg, Mölndal och Borås planeras in i samma produktionsresa." },
      { title: "Samma manér som övriga landet", body: "Bilderna matchar serier tagna i Stockholm och Malmö." },
    ],
    faq: [
      { q: "Vad kostar en företagsfotograf i Göteborg?", a: "Priset sätts per pass: en halvdag räcker till 10–20 porträtt, en heldag till 20–40 eller till en kombination av porträtt och miljöbilder. Ni får fast pris innan bokning." },
      { q: "Kan ni fotografera i verkstad eller hamn?", a: "Ja. Vi har egen skyddsutrustning och planerar dagen kring skiftbyten och säkerhetsrutiner tillsammans med er platsansvarige." },
      { q: "Reser ni från Stockholm?", a: "Ja, och resan ingår när uppdraget fyller minst en halvdag. Kortare uppdrag offereras med resa till självkostnad." },
      { q: "Kan flera anläggningar fotograferas samma vecka?", a: "Ja. Göteborg, Mölndal och Borås planeras normalt in i samma produktionsresa." },
    ],
    related: related("/foto/foretagsfotograf-goteborg"),
  },
  {
    slug: "portrattfotograf-goteborg",
    path: "/foto/portrattfotograf-goteborg",
    serviceKey: "portratt",
    citySlug: "goteborg",
    city: "Göteborg",
    region: "Västra Götaland",
    crumb: "Porträttfotograf Göteborg",
    nav: "Porträttfotograf Göteborg",
    metaTitle: "Porträttfotograf Göteborg — headshots hos er | Fenny",
    description:
      "Porträttfotograf i Göteborg för headshots och miljöporträtt. Mobil studio hos er i centrum eller Mölndal. Leverans inom 3–5 arbetsdagar.",
    eyebrow: "Porträttfotograf Göteborg",
    h1: "Porträttfotograf i Göteborg — hela teamet i samma manér",
    lede:
      "Headshots för sajt och LinkedIn, miljöporträtt i den verksamhet ni faktiskt bedriver — fotograferade på plats så att ingen behöver lägga en halvdag på resa.",
    question: "Vad kostar en porträttfotograf i Göteborg?",
    quickAnswer:
      "En porträttfotograf i Göteborg prissätts per pass. En halvdag räcker till 10–20 porträtt och en heldag till 20–40. Fenny kommer till er med mobil studio i centrum, Lindholmen, Gårda, Mölndal eller på Hisingen. " +
      DELIVERY +
      " " +
      QUOTE,
    serviceType: "Porträttfotografering i Göteborg",
    gallery: "portratt",
    galleryHeading: "Porträtt ur portfolion",
    knowsAbout: [
      "Porträttfotograf Göteborg",
      "Headshots Göteborg",
      "Miljöporträtt",
      "Företagsporträtt Göteborg",
    ],
    localAngle: {
      heading: "Vad Göteborgsbolagen beställer",
      paragraphs: [
        "Den vanligaste beställningen är hela personalen på en dag inför en sajtomgörning, ofta i bolag som vuxit snabbt och där porträtten i dag är en blandning av mobilbilder och gamla studiobilder.",
        "Den näst vanligaste är miljöporträtt: ingenjören vid maskinen, projektledaren i hamnen, forskaren i labbet. Det är de bilderna som gör skillnad i offerter och rekryteringsannonser, eftersom de visar vad bolaget faktiskt gör.",
        "Konsult- och techbolagen på Lindholmen beställer oftast neutrala headshots med samma utsnitt för alla, plus ett friare alternativ för dem som syns utåt i sälj och rekrytering.",
      ],
    },
    logistics: {
      heading: "Så genomförs porträttdagen",
      paragraphs: [
        "Vi bygger mobil studio i ett konferensrum, sätter ett tidsschema i femminutersluckor per avdelning och skickar en kort klädguide i förväg. Det som behövs är ett rum på ungefär tre gånger tre meter och ett eluttag.",
        "Miljöporträtt läggs efter studiopasset, när vi vet hur mycket tid som finns kvar. Där rekar vi ett par platser i lokalerna på morgonen och håller oss till dem för att spara tid.",
        "Alla får se ett par bilder direkt i skärmen. Urvalet görs sedan i ett digitalt galleri och de valda bilderna retuscheras varsamt innan leverans i kvadrat, stående och högupplöst för tryck.",
      ],
    },
    bullets: [
      { title: "Ingen restid för teamet", body: "Mobil studio i era lokaler betyder att medarbetarna bara lämnar skrivbordet i fem minuter." },
      { title: "Miljöporträtt på köpet", body: "Studiopass och miljöporträtt planeras i samma dag när tiden räcker." },
      { title: "Klädguide i förväg", body: "En kort instruktion gör att serien hänger ihop utan efterarbete." },
      { title: "Matchar övriga kontor", body: "Samma ljus och beskärning som porträtt tagna i Stockholm eller Malmö." },
    ],
    faq: [
      { q: "Vad kostar porträttfotografering i Göteborg?", a: "Priset sätts per pass: en halvdag räcker till 10–20 personer och en heldag till 20–40. Ni får fast pris innan bokning." },
      { q: "Kommer ni till vårt kontor?", a: "Ja. Vi bygger mobil studio i ett konferensrum — cirka 45 minuters riggning och ett rum på ungefär tre gånger tre meter." },
      { q: "Kan vi kombinera headshots och miljöporträtt?", a: "Ja, det ryms normalt i samma dag med cirka fem minuters påslag per person." },
      { q: "Hur mycket retuscheras bilderna?", a: "Varsamt: hy och ljus jämnas ut, ansiktsdrag ändras inte." },
    ],
    related: related("/foto/portrattfotograf-goteborg"),
  },
  {
    slug: "produktfotograf-goteborg",
    path: "/foto/produktfotograf-goteborg",
    serviceKey: "produkt",
    citySlug: "goteborg",
    city: "Göteborg",
    region: "Västra Götaland",
    crumb: "Produktfotograf Göteborg",
    nav: "Produktfotograf Göteborg",
    metaTitle: "Produktfotograf Göteborg — packshots och e-handel | Fenny",
    description:
      "Produktfotograf i Göteborg för packshots, komponenter och e-handelsbilder. Studio eller fotografering på plats i produktionen.",
    eyebrow: "Produktfotograf Göteborg",
    h1: "Produktfotograf i Göteborg — även när produkten väger ett ton",
    lede:
      "Packshots på vitt för e-handel, detaljbilder på komponenter och fotografering på plats när produkten sitter fast i en produktionslinje.",
    question: "Vad kostar produktfotografering i Göteborg?",
    quickAnswer:
      "Produktfoto prissätts per produkt vid repetitiva upplägg och per dag när bilderna är unika. I Göteborg fotograferas mindre produkter i studio och stora maskiner på plats i produktionen, där ljuset byggs upp i den befintliga miljön. " +
      DELIVERY +
      " " +
      QUOTE,
    serviceType: "Produktfotografering i Göteborg",
    gallery: "studio",
    galleryHeading: "Produkt- och industribilder",
    knowsAbout: [
      "Produktfotograf Göteborg",
      "Industrifoto",
      "Packshots",
      "Komponentfoto",
    ],
    localAngle: {
      heading: "Produkter som inte får plats i en studio",
      paragraphs: [
        "En stor del av produktfotot i Göteborgsregionen handlar om maskiner, fordonskomponenter och utrustning som inte går att skicka till en studio. Då kommer studion till produkten: vi bygger ljus i hallen och skärmar av bakgrunden i stället för att flytta ett par ton stål.",
        "Komponentfoto ställer krav på skärpedjup. Vi arbetar med fokusstacking när hela detaljen ska vara knivskarp, vilket är standard i teknisk dokumentation och offertunderlag.",
        "Konsumentprodukter och e-handelssortiment fotograferas i stället i studio, med samma packshot-manér som våra Stockholmsuppdrag så att bildbanken hänger ihop över hela bolaget.",
      ],
    },
    logistics: {
      heading: "Så planeras uppdraget",
      paragraphs: [
        "Vi börjar med en bildlista: vilka produkter, hur många vinklar per produkt, vilka detaljer som är säljande och vilka format bilderna ska användas i.",
        "Vid fotografering på plats rekar vi hallen i förväg eller går igenom bilder ni skickar, så att rätt ljusutrustning tas med. Skyddsrutiner och skiftbyten planeras med er platsansvarige.",
        "Efterarbetet innehåller frilägg där det behövs, färgmatchning mot faktisk produkt och export i webb- och tryckupplösning, namngivet efter era artikelnummer.",
      ],
    },
    bullets: [
      { title: "Studio eller på plats", body: "Stora maskiner fotograferas i hallen med uppbyggt ljus och avskärmad bakgrund." },
      { title: "Fokusstacking", body: "Hela detaljen skarp — standard i teknisk dokumentation och offertunderlag." },
      { title: "Färgmatchning", body: "Bilden matchas mot faktisk produkt, inte mot skärmens gissning." },
      { title: "Namngivna filer", body: "Leverans efter era artikelnummer vid större sortiment." },
    ],
    faq: [
      { q: "Kan ni fotografera stora maskiner?", a: "Ja. Vi bygger ljus på plats i produktionshallen och skärmar av bakgrunden i stället för att flytta produkten." },
      { q: "Vad kostar produktfoto i Göteborg?", a: "Repetitiva upplägg prissätts per produkt, unika bilder per dag. Ni får fast pris innan bokning." },
      { q: "Klarar ni tekniska detaljbilder?", a: "Ja, med fokusstacking när hela detaljen ska vara skarp." },
      { q: "Hanterar ni sekretess?", a: "Ja. Ritningar, skärmar och prototyper täcks eller flyttas, och ni godkänner urvalet innan publicering." },
    ],
    related: related("/foto/produktfotograf-goteborg"),
  },
  {
    slug: "eventfotograf-goteborg",
    path: "/foto/eventfotograf-goteborg",
    serviceKey: "event",
    citySlug: "goteborg",
    city: "Göteborg",
    region: "Västra Götaland",
    crumb: "Eventfotograf Göteborg",
    nav: "Eventfotograf Göteborg",
    metaTitle: "Eventfotograf Göteborg — konferens och mässa | Fenny",
    description:
      "Eventfotograf i Göteborg för mässor på Svenska Mässan, konferenser och kundträffar. Första urval samma kväll, hela leveransen inom 3–5 arbetsdagar.",
    eyebrow: "Eventfotograf Göteborg",
    h1: "Eventfotograf i Göteborg — mässa, konferens och kundträff",
    lede:
      "Monterbilder som går att använda i uppföljningen, talarbilder till kanalerna och en arkivserie som lever vidare i nästa års marknadsföring.",
    question: "Vad kostar en eventfotograf i Göteborg?",
    quickAnswer:
      "Eventfoto prissätts per timmar på plats. En halvdag ger normalt 60–100 redigerade bilder och en heldag 150–250. I Göteborg är mässuppdrag på Svenska Mässan vanligast, följt av konferenser och kundträffar. " +
      QUOTE,
    serviceType: "Eventfotografering i Göteborg",
    gallery: "kampanj",
    galleryHeading: "Eventbilder ur portfolion",
    knowsAbout: [
      "Eventfotograf Göteborg",
      "Mässfotograf",
      "Konferensfotograf Göteborg",
      "Fotograf kundträff",
    ],
    localAngle: {
      heading: "Mässor och konferenser i Göteborg",
      paragraphs: [
        "Svenska Mässan står för en stor del av eventuppdragen i Göteborg. Där är ljuset hårt och utrymmet i montern begränsat, så bildlistan sätts i förväg: monterhelhet, produktdemo, möten i montern, talare och personalbilder.",
        "Konferenser i staden hålls ofta i lokaler med starka scenljus och mörk publikdel. Vi fotograferar med snabba objektiv i stället för blixt, så att stämningen finns kvar och talaren inte blir störd.",
        "Kundträffar och interna dagar dokumenteras reportagemässigt, med ett par planerade gruppbilder inlagda i körschemat medan alla fortfarande är samlade.",
      ],
    },
    logistics: {
      heading: "Under och efter eventet",
      paragraphs: [
        "Vi går igenom körschemat i förväg och markerar det som absolut måste finnas med. Under dagen kan ett första urval levereras löpande så att marknad kan publicera medan eventet pågår.",
        "Vid mässor levererar vi ofta monterbilder redan efter första dagen — de används i uppföljningsmejl till besökarna medan mässan fortfarande pågår.",
        "Efter eventet väljer ni ur ett galleri, urvalet färgsätts och levereras i liggande, stående och kvadratiska beskärningar.",
      ],
    },
    bullets: [
      { title: "Van vid Svenska Mässan", body: "Bildlista för monter, demo, möten och talare sätts före mässdagen." },
      { title: "Bilder under pågående event", body: "Första urvalet kan levereras samma dag för publicering och uppföljning." },
      { title: "Utan blixt när det går", body: "Snabba objektiv gör att talare och publik inte störs." },
      { title: "Film parallellt", body: "Aftermovie och korta klipp kan produceras av samma team." },
    ],
    faq: [
      { q: "Vad kostar en eventfotograf i Göteborg?", a: "Priset sätts per timmar på plats. En halvdag ger normalt 60–100 redigerade bilder och en heldag 150–250. Ni får fast pris innan bokning." },
      { q: "Fotograferar ni på Svenska Mässan?", a: "Ja, mässuppdrag är den vanligaste eventtypen vi gör i Göteborg." },
      { q: "Kan vi få monterbilder samma dag?", a: "Ja. Ett första urval kan levereras redan efter första mässdagen för uppföljningsmejl." },
      { q: "Behöver vi ackreditering till oss?", a: "Ofta ja — vi behöver utställarbricka eller motsvarande, vilket ni ordnar via arrangören." },
    ],
    related: related("/foto/eventfotograf-goteborg"),
  },

  // ------------------------------------------------------------------- Malmö
  {
    slug: "foretagsfotograf-malmo",
    path: "/foto/foretagsfotograf-malmo",
    serviceKey: "foretagsfoto",
    citySlug: "malmo",
    city: "Malmö",
    region: "Skåne län",
    crumb: "Företagsfotograf Malmö",
    nav: "Företagsfotograf Malmö",
    metaTitle: "Företagsfotograf Malmö — porträtt och kampanj | Fenny",
    description:
      "Företagsfotograf i Malmö för porträtt, miljöbilder och kampanjbilder. Mobil studio hos er i Västra hamnen, Hyllie eller Lund.",
    eyebrow: "Företagsfotograf Malmö",
    h1: "Företagsfotograf i Malmö — bilder för hela Öresundsregionen",
    lede:
      "Porträtt, miljöbilder och kampanjmaterial för bolag i Malmö, Lund och Helsingborg — och för koncerner som har kontor på båda sidor sundet.",
    question: "Vad gör en företagsfotograf i Malmö?",
    quickAnswer:
      "En företagsfotograf i Malmö fotograferar porträtt, miljöbilder och kampanjbilder för företagets egna kanaler. Fenny arbetar med mobil studio i Västra hamnen, Hyllie, centrum, Lund och Helsingborg, och tar även uppdrag i Köpenhamn i samma resa. " +
      DELIVERY +
      " " +
      QUOTE,
    serviceType: "Företagsfotografering i Malmö",
    gallery: "portratt",
    galleryHeading: "Företagsbilder ur portfolion",
    knowsAbout: [
      "Företagsfotograf Malmö",
      "Företagsporträtt Malmö",
      "Miljöbilder kontor",
      "Fotograf Öresundsregionen",
    ],
    localAngle: {
      heading: "Öresund som ett upptagningsområde",
      paragraphs: [
        "Malmöbolagen har ofta verksamhet spridd över sundet: huvudkontor i Malmö eller Hyllie, forskning i Lund och en koncernfunktion i Köpenhamn. Vi planerar fotograferingen som en resa i stället för tre uppdrag, vilket både sänker kostnaden och gör att bilderna ser likadana ut oavsett kontor.",
        "Life science-bolagen kring Ideon och Medicon Village ställer särskilda krav: tillträde till labb, skyddskläder och rutiner för hur utrustning rengörs. Det planeras med er platsansvarige före fotodagen.",
        "Kreativa bolag i Västra hamnen beställer i sin tur oftare miljöporträtt och kampanjbilder än rena headshots — bilder som visar arbetssätt snarare än ansikten mot vit bakgrund.",
      ],
    },
    logistics: {
      heading: "Så genomförs uppdraget i Malmö",
      paragraphs: [
        "Porträtten tas med mobil studio i ett konferensrum, cirka 45 minuters riggning och ett rum på ungefär tre gånger tre meter. Ett tidsschema per avdelning gör att ingen väntar.",
        "Miljöbilder och kampanjbilder rekas på morgonen — ett par platser i lokalerna eller i närområdet — och fotograferas när ljuset är som bäst.",
        "Uppdrag i Köpenhamn offereras med bropassage inkluderad och läggs normalt i anslutning till en fotodag i Malmö eller Lund.",
      ],
    },
    bullets: [
      { title: "Malmö, Lund och Köpenhamn", body: "Kontor på båda sidor sundet planeras in i samma produktionsresa." },
      { title: "Van vid labbmiljö", body: "Tillträde, skyddskläder och rengöringsrutiner planeras före fotodagen." },
      { title: "Mobil studio", body: "Hela teamet fotograferas på plats utan att någon behöver resa." },
      { title: "Samma manér som övriga landet", body: "Bilderna matchar serier tagna i Stockholm och Göteborg." },
    ],
    faq: [
      { q: "Vad kostar en företagsfotograf i Malmö?", a: "Priset sätts per pass: en halvdag räcker till 10–20 porträtt och en heldag till 20–40 eller en kombination av porträtt och miljöbilder. Ni får fast pris innan bokning." },
      { q: "Fotograferar ni i Lund och Helsingborg?", a: "Ja, båda ingår i Malmöregionen och kan läggas i samma fotoresa." },
      { q: "Tar ni uppdrag i Köpenhamn?", a: "Ja. Uppdrag över bron offereras med bropassage inkluderad och planeras i anslutning till en dag i Malmö eller Lund." },
      { q: "Kan ni fotografera i labbmiljö?", a: "Ja. Tillträde, skyddskläder och hantering av utrustning planeras med er platsansvarige före fotodagen." },
    ],
    related: related("/foto/foretagsfotograf-malmo"),
  },
  {
    slug: "portrattfotograf-malmo",
    path: "/foto/portrattfotograf-malmo",
    serviceKey: "portratt",
    citySlug: "malmo",
    city: "Malmö",
    region: "Skåne län",
    crumb: "Porträttfotograf Malmö",
    nav: "Porträttfotograf Malmö",
    metaTitle: "Porträttfotograf Malmö — headshots hos er | Fenny",
    description:
      "Porträttfotograf i Malmö för headshots och miljöporträtt. Mobil studio hos er i Västra hamnen eller Lund. Leverans inom 3–5 arbetsdagar.",
    eyebrow: "Porträttfotograf Malmö",
    h1: "Porträttfotograf i Malmö — samma ljus för hela organisationen",
    lede:
      "Headshots för sajt och LinkedIn, ledningsgruppsporträtt inför rapporten och miljöporträtt från den verksamhet ni faktiskt bedriver.",
    question: "Vad kostar en porträttfotograf i Malmö?",
    quickAnswer:
      "En porträttfotograf i Malmö prissätts per pass. En halvdag räcker till 10–20 porträtt och en heldag till 20–40. Fenny kommer med mobil studio till Västra hamnen, Hyllie, centrum, Limhamn eller Lund. " +
      DELIVERY +
      " " +
      QUOTE,
    serviceType: "Porträttfotografering i Malmö",
    gallery: "portratt",
    galleryHeading: "Porträtt ur portfolion",
    knowsAbout: [
      "Porträttfotograf Malmö",
      "Headshots Malmö",
      "Miljöporträtt",
      "Företagsporträtt Malmö",
    ],
    localAngle: {
      heading: "Porträtt i Malmö och Lund",
      paragraphs: [
        "Forskningsbolagen i Lund beställer ofta porträtt av seniora forskare inför publiceringar och konferenser, gärna med ett miljöporträtt från labbet som alternativ till studiobilden.",
        "I Malmö är den vanligaste beställningen hela personalen på en dag: byråer, techbolag och konsulthus som vill byta ut en spretig samling gamla bilder mot en enhetlig serie.",
        "Många bolag i regionen har medarbetare i Köpenhamn. Dem fotograferar vi antingen i samma resa eller i ett andra pass, med identisk ljusuppsättning så att bilderna hänger ihop.",
      ],
    },
    logistics: {
      heading: "Så genomförs porträttdagen",
      paragraphs: [
        "Mobil studio byggs i ett konferensrum på cirka 45 minuter. Tidsschema i femminutersluckor och en kort klädguide i förväg gör dagen effektiv.",
        "Alla får se ett par bilder direkt på skärmen. Det gör stor skillnad för dem som ogärna blir fotograferade, och minskar antalet omtagningar i efterhand.",
        "Urvalet görs i ett digitalt galleri, retuscheras varsamt och levereras i kvadrat för intranät och LinkedIn, stående för sajten och högupplöst för tryck.",
      ],
    },
    bullets: [
      { title: "5–10 minuter per person", body: "Ett tight schema gör att en heldag räcker till 20–40 porträtt." },
      { title: "Lund och Köpenhamn", body: "Medarbetare på andra orter fotograferas med identisk ljusuppsättning." },
      { title: "Miljöporträtt som alternativ", body: "Ett friare porträtt från labb eller kontor ryms i samma pass." },
      { title: "Varsam retusch", body: "Hy och ljus jämnas ut — ansiktsdrag rörs inte." },
    ],
    faq: [
      { q: "Vad kostar porträttfotografering i Malmö?", a: "Priset sätts per pass: en halvdag räcker till 10–20 personer och en heldag till 20–40. Ni får fast pris innan bokning." },
      { q: "Fotograferar ni i Lund?", a: "Ja, Lund ingår i Malmöregionen och kan fotograferas samma dag eller i samma resa." },
      { q: "Kan medarbetare i Köpenhamn fotograferas?", a: "Ja, med samma ljusuppsättning så att bilderna matchar den svenska serien." },
      { q: "Hur snabbt får vi bilderna?", a: "Urvalsgalleri inom två arbetsdagar och retuscherade bilder inom 3–5 arbetsdagar." },
    ],
    related: related("/foto/portrattfotograf-malmo"),
  },
  {
    slug: "produktfotograf-malmo",
    path: "/foto/produktfotograf-malmo",
    serviceKey: "produkt",
    citySlug: "malmo",
    city: "Malmö",
    region: "Skåne län",
    crumb: "Produktfotograf Malmö",
    nav: "Produktfotograf Malmö",
    metaTitle: "Produktfotograf Malmö — e-handel och packshots | Fenny",
    description:
      "Produktfotograf i Malmö för e-handelsbilder, packshots och förpackningsbilder. Studio eller på plats, leverans inom 3–5 arbetsdagar.",
    eyebrow: "Produktfotograf Malmö",
    h1: "Produktfotograf i Malmö — packshots, förpackning och sortiment",
    lede:
      "Rena packshots för e-handel, förpackningsbilder för dagligvaruhandeln och sortimentsserier för varumärken som säljer via butik och marknadsplatser.",
    question: "Vad kostar produktfotografering i Malmö?",
    quickAnswer:
      "Produktfoto prissätts per produkt vid repetitiva upplägg och per dag när bilderna är unika. En studiodag räcker till 15–40 packshots, och färre när varje produkt kräver egen ljussättning eller flera vinklar. " +
      DELIVERY +
      " " +
      QUOTE,
    serviceType: "Produktfotografering i Malmö",
    gallery: "studio",
    galleryHeading: "Produkt- och förpackningsbilder",
    knowsAbout: [
      "Produktfotograf Malmö",
      "Förpackningsfoto",
      "Packshots",
      "E-handelsbilder",
    ],
    localAngle: {
      heading: "Handel och e-handel i Skåne",
      paragraphs: [
        "Skåne har en tät handels- och dagligvarusektor, och en stor del av produktuppdragen i Malmö handlar om förpackningar och sortiment som ska se likadana ut i butikens digitala hylla som i den fysiska.",
        "E-handelsbolagen i regionen behöver i stället volym: hela sortiment fotograferade i samma manér, med bakgrund och marginaler som klarar marknadsplatsernas krav.",
        "Förpackningsbilder ligger mitt emellan — de ska visa etiketten korrekt läsbar, matcha tryckfärgen och samtidigt fungera som säljande bild i butikens digitala hylla.",
      ],
    },
    logistics: {
      heading: "Så planeras fotodagen",
      paragraphs: [
        "Bildlistan sätts först: antal produkter, vinklar per produkt, vilka detaljer som ska synas och i vilka format bilderna ska användas.",
        "Vid komplexa produkter planeras riggning och vinklar i förväg, och referensexemplar skickas till studion innan fotodagen så att färg och material kan provljussättas.",
        "Efterarbetet innehåller frilägg, färgmatchning mot faktisk produkt och export i webb- och tryckupplösning namngivet efter era artikelnummer.",
      ],
    },
    bullets: [
      { title: "Förpackning i fokus", body: "Etiketten läsbar, tryckfärgen matchad och bilden säljande i den digitala hyllan." },
      { title: "Volym för e-handel", body: "15–40 packshots per studiodag i ett upprepbart manér." },
      { title: "Korrekt färg", body: "Bilden matchas mot faktisk produkt och tryckfärg, inte mot skärmens gissning." },
      { title: "Rätt format direkt", body: "Export enligt marknadsplatsernas krav på bakgrund och marginaler." },
    ],
    faq: [
      { q: "Vad kostar produktfoto i Malmö?", a: "Repetitiva upplägg prissätts per produkt, unika bilder per dag. Ni får fast pris innan bokning." },
      { q: "Hur många produkter hinns med på en dag?", a: "15–40 packshots i ett repetitivt upplägg, färre när varje produkt kräver egen ljussättning eller flera vinklar." },
      { q: "Kan ni frilägga bilderna?", a: "Ja, frilägg och färgmatchning ingår i efterarbetet och specificeras i offerten." },
      { q: "Hur levereras filerna?", a: "Frilagda och färgmatchade i webb- och tryckupplösning, namngivna efter era artikelnummer." },
    ],
    related: related("/foto/produktfotograf-malmo"),
  },
  {
    slug: "eventfotograf-malmo",
    path: "/foto/eventfotograf-malmo",
    serviceKey: "event",
    citySlug: "malmo",
    city: "Malmö",
    region: "Skåne län",
    crumb: "Eventfotograf Malmö",
    nav: "Eventfotograf Malmö",
    metaTitle: "Eventfotograf Malmö — konferens och mässa | Fenny",
    description:
      "Eventfotograf i Malmö för konferenser, mässor och kundträffar i Malmö, Lund och Helsingborg. Första urval samma kväll, hela leveransen inom 3–5 arbetsdagar.",
    eyebrow: "Eventfotograf Malmö",
    h1: "Eventfotograf i Malmö — dokumentation ni kan publicera direkt",
    lede:
      "Konferenser, seminarier och kundträffar i Malmö, Lund och Helsingborg — fotograferade så att både kanalbilder och arkivmaterial finns när dagen är slut.",
    question: "Vad kostar en eventfotograf i Malmö?",
    quickAnswer:
      "Eventfoto prissätts per timmar på plats. En halvdag ger normalt 60–100 redigerade bilder och en heldag 150–250. Ett första urval kan levereras samma kväll för publicering, resten inom 3–5 arbetsdagar. " +
      QUOTE,
    serviceType: "Eventfotografering i Malmö",
    gallery: "kampanj",
    galleryHeading: "Eventbilder ur portfolion",
    knowsAbout: [
      "Eventfotograf Malmö",
      "Konferensfotograf Malmö",
      "Fotograf seminarium",
      "Mässfotograf Skåne",
    ],
    localAngle: {
      heading: "Konferenser i Malmö, Lund och Helsingborg",
      paragraphs: [
        "Många av eventen i regionen är kunskapsdrivna: forskarseminarier i Lund, branschdagar i Malmö och kundträffar med inbjudna talare. Där är talarbilder, publikbilder och samtalen i pauserna det som faktiskt används efteråt.",
        "Lokalerna varierar kraftigt — från universitetsaulor med dagsljus till konferensvåningar med starka scenljus. Vi rekar rummet före första passet och sätter kamerainställningarna efter det, i stället för att lösa det med blixt.",
        "Event med deltagare från Danmark kräver ofta att materialet publiceras på både svenska och engelska samma dag. Vi levererar därför bildurvalet i format som passar båda kanalerna direkt.",
      ],
    },
    logistics: {
      heading: "Under och efter eventet",
      paragraphs: [
        "Körschemat gås igenom i förväg och de ögonblick som måste finnas markeras: keynote, panel, prisutdelning och gruppbilder.",
        "Ett första urval kan levereras löpande under dagen, färdigt för publicering medan eventet pågår.",
        "Efter eventet väljer ni ur ett galleri. Urvalet färgsätts och levereras i liggande, stående och kvadratisk beskärning så att samma bild kan gå i flera kanaler.",
      ],
    },
    bullets: [
      { title: "Bilder samma kväll", body: "Ett första urval kan levereras under pågående event." },
      { title: "Reker lokalen först", body: "Kamerainställningar sätts efter rummets ljus i stället för att lösas med blixt." },
      { title: "Hela regionen", body: "Malmö, Lund och Helsingborg utan extra resekostnad." },
      { title: "Film parallellt", body: "Aftermovie och klipp till kanalerna kan produceras av samma team." },
    ],
    faq: [
      { q: "Vad kostar en eventfotograf i Malmö?", a: "Priset sätts per timmar på plats. En halvdag ger normalt 60–100 redigerade bilder och en heldag 150–250. Ni får fast pris innan bokning." },
      { q: "Täcker ni Lund och Helsingborg?", a: "Ja, båda ingår i Malmöregionen utan extra resekostnad." },
      { q: "Kan vi få bilder under dagen?", a: "Ja. Ett första urval levereras löpande så att marknad kan publicera medan eventet pågår." },
      { q: "Fotograferar ni utan blixt?", a: "Ja när ljuset tillåter — vi rekar lokalen först och arbetar med snabba objektiv." },
    ],
    related: related("/foto/eventfotograf-malmo"),
  },
];

export const FOTO_SERVICE_CITY_PATHS = fotoServiceCities.map((p) => p.path);

export function fotoServiceCitiesFor(citySlug: string) {
  return fotoServiceCities.filter((p) => p.citySlug === citySlug);
}
